import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TableFooter,
  Tabs,
  Tab,
  Tooltip as MuiTooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { useTranslate } from 'react-admin';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { TableChart, ShowChart } from '@mui/icons-material';
import { ReportLayout } from '../shared/ReportLayout';
import { ReportFilters } from '../shared/ReportFilters';
import { UsageHeatmap } from './components/UsageHeatmap';
import { useReportData } from '../../../hooks/useReportData';
import { useReportExport } from '../../../hooks/useReportExport';
import { useCanSeeTotalSessions } from '../../../hooks/useCanSeeTotalSessions';
import { formatNumber, formatDate, getDateRangePresets } from '../../../utils/reportHelpers';
import { API_CONFIG } from '../../../config/api';

interface PeakSummary {
  peak_hour: string;
  peak_hour_sessions: number;
  peak_concurrent_sessions: number;
  peak_load_kw: number;
  total_sessions: number;
  charged_sessions: number;
}

interface HourlyDistribution {
  hour: number;
  hour_label: string;
  sessions: number;
  charged_sessions: number;
}

interface ConcurrentSession {
  timestamp: string;
  concurrent_sessions: number;
}

interface PeakReportData {
  summary: PeakSummary;
  hourly_distribution: HourlyDistribution[];
  weekly_heatmap: any[][];
  concurrent_sessions_over_time: ConcurrentSession[];
}

// TabPanel component
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

export const PeakUsageAnalysisReport: React.FC = () => {
  const translate = useTranslate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const presets = getDateRangePresets();
  const canSeeTotalSessions = useCanSeeTotalSessions();

  // Filters state
  const [startDate, setStartDate] = useState(presets.last30Days.start);
  const [endDate, setEndDate] = useState(presets.last30Days.end);
  const [siteId, setSiteId] = useState('');
  const [vendorId, setVendorId] = useState('');

  // Filter options state (no chargers for peak usage - site level analysis)
  const [sites, setSites] = useState<Array<{ id: string; name: string }>>([]);
  const [vendors, setVendors] = useState<Array<{ id: string; name: string }>>([]);

  // Tab state (0 = table, 1 = charts)
  const [tabValue, setTabValue] = useState(0);

  // Table sorting state
  const [orderBy, setOrderBy] = useState<keyof HourlyDistribution>('hour');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');

  // Helper function to get auth headers
  const getAuthHeaders = () => {
    const tokenData = localStorage.getItem('ev_charging_auth_tokens_v2');
    const token = tokenData ? JSON.parse(tokenData).accessToken : null;

    if (!token) {
      console.error('No auth token found');
      return null;
    }

    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  };

  // Fetch vendors on mount (vendors list is independent)
  useEffect(() => {
    const fetchVendors = async () => {
      try {
        const headers = getAuthHeaders();
        if (!headers) return;

        const response = await fetch(`${API_CONFIG.baseURL}/api/backend/reports/filters/vendors`, {
          headers,
        });
        const data = await response.json();
        setVendors(data.data || []);
      } catch (error) {
        console.error('Error fetching vendors:', error);
      }
    };

    fetchVendors();
  }, []);

  // Fetch sites when vendor changes
  useEffect(() => {
    const fetchSites = async () => {
      try {
        const headers = getAuthHeaders();
        if (!headers) return;

        // Build URL with vendor_id parameter if vendor is selected
        const url = vendorId
          ? `${API_CONFIG.baseURL}/api/backend/reports/filters/sites?vendor_id=${vendorId}`
          : `${API_CONFIG.baseURL}/api/backend/reports/filters/sites`;

        const response = await fetch(url, { headers });
        const data = await response.json();
        setSites(data.data || []);

        // Reset site selection if it's no longer in the filtered list
        if (siteId && !data.data?.some((s: any) => s.id === siteId)) {
          setSiteId('');
        }
      } catch (error) {
        console.error('Error fetching sites:', error);
      }
    };

    fetchSites();
  }, [vendorId]); // Re-fetch when vendor changes

  // Fetch data
  const { data, loading, error, refetch } = useReportData<PeakReportData>({
    endpoint: '/api/backend/reports/peak-usage',
    filters: {
      start_date: startDate,
      end_date: endDate,
      ...(siteId && { site_id: siteId }),
      ...(vendorId && { vendor_id: vendorId }),
    },
  });

  // Transform weekly heatmap data for UsageHeatmap component
  const transformHeatmapData = (weeklyHeatmap: any[][]) => {
    if (!weeklyHeatmap || weeklyHeatmap.length === 0) return [];

    const flatData: { hour: number; day: number; count: number }[] = [];

    weeklyHeatmap.forEach((dayData, dayIndex) => {
      dayData.forEach((hourData) => {
        flatData.push({
          hour: hourData.hour,
          day: dayIndex,
          count: hourData.sessions,
        });
      });
    });

    return flatData;
  };

  // Export functionality
  const { exportToCSV, exportToExcel, exporting } = useReportExport();

  const handleExportCSV = () => {
    if (!data?.hourly_distribution) return;

    exportToCSV(
      data.hourly_distribution,
      'peak-usage',
      [
        { key: 'hour_label', label: 'Hour' },
        ...(canSeeTotalSessions ? [{ key: 'sessions', label: 'Total Sessions', format: 'number' as const }] : []),
        { key: 'charged_sessions', label: 'Charged Sessions', format: 'number' },
      ]
    );
  };

  const handleExportExcel = async () => {
    if (!data) return;

    // Build filters object
    const filters: any = {
      startDate,
      endDate,
    };

    if (siteId) {
      const selectedSite = sites.find(s => s.id === siteId);
      if (selectedSite) {
        filters.site = { id: siteId, name: selectedSite.name };
      }
    }

    if (vendorId) {
      const selectedVendor = vendors.find(v => v.id === vendorId);
      if (selectedVendor) {
        filters.vendor = { id: vendorId, name: selectedVendor.name };
      }
    }

    await exportToExcel(
      data.hourly_distribution,
      'peak-usage',
      {
        columns: [
          { key: 'hour_label', label: translate('reports.table.hour', { fallback: 'Hour' }) },
          ...(canSeeTotalSessions ? [{ key: 'sessions', label: translate('reports.kpi.totalSessions', { fallback: 'Total Sessions' }), format: 'number' as const }] : []),
          { key: 'charged_sessions', label: translate('reports.kpi.chargedSessions', { fallback: 'Charged Sessions' }), format: 'number' },
        ],
        summary: {
          title: translate('reports.peak.title', { fallback: 'Peak Usage Analysis' }),
          data: [
            {
              label: translate('reports.kpi.peakHour', { fallback: 'Peak Hour' }),
              value: data.summary.peak_hour
            },
            {
              label: translate('reports.kpi.peakHourSessions', { fallback: 'Peak Hour Sessions' }),
              value: formatNumber(data.summary.peak_hour_sessions, 0)
            },
            {
              label: translate('reports.kpi.peakConcurrentSessions', { fallback: 'Peak Concurrent Sessions' }),
              value: formatNumber(data.summary.peak_concurrent_sessions, 0)
            },
            {
              label: translate('reports.kpi.peakLoad', { fallback: 'Peak Load' }),
              value: formatNumber(data.summary.peak_load_kw, 2) + ' kW'
            },
            ...(canSeeTotalSessions ? [{
              label: translate('reports.kpi.totalSessions', { fallback: 'Total Sessions' }),
              value: formatNumber(data.summary.total_sessions, 0)
            }] : []),
            {
              label: translate('reports.kpi.chargedSessions', { fallback: 'Charged Sessions' }),
              value: formatNumber(data.summary.charged_sessions, 0)
            },
          ]
        },
        sheetName: translate('reports.tabs.dataTable', { fallback: 'Data' }),
        includeTimestamp: true,
        translate: translate,
        filters: filters
      }
    );
  };

  // Sort data
  const sortedData = useMemo(() => {
    if (!data?.hourly_distribution) return [];

    return [...data.hourly_distribution].sort((a, b) => {
      const aValue = a[orderBy];
      const bValue = b[orderBy];

      if (aValue < bValue) return order === 'asc' ? -1 : 1;
      if (aValue > bValue) return order === 'asc' ? 1 : -1;
      return 0;
    });
  }, [data?.hourly_distribution, orderBy, order]);

  // Calculate totals for footer
  const totals = useMemo(() => {
    if (!data?.hourly_distribution || data.hourly_distribution.length === 0) {
      return {
        sessions: 0,
        charged_sessions: 0,
      };
    }

    return {
      sessions: data.hourly_distribution.reduce((sum, row) => sum + row.sessions, 0),
      charged_sessions: data.hourly_distribution.reduce((sum, row) => sum + row.charged_sessions, 0),
    };
  }, [data?.hourly_distribution]);

  const handleSort = (property: keyof HourlyDistribution) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  return (
    <ReportLayout
      title={translate('reports.peak.title', { fallback: 'Peak Usage Analysis' })}
      description={translate('reports.peak.subtitle', { fallback: 'Load distribution and peak demand analysis' })}
      filters={
        <ReportFilters
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          siteId={siteId}
          onSiteIdChange={setSiteId}
          vendorId={vendorId}
          onVendorIdChange={setVendorId}
          sites={sites}
          vendors={vendors}
          showSiteFilter={true}
          showVendorFilter={true}
          showChargerFilter={false}
          onRefresh={refetch}
          showGroupByFilter={false}
        />
      }
      exportButtons={{
        onExportCSV: handleExportCSV,
        onExportExcel: handleExportExcel,
        exporting,
        disabled: !data || loading,
      }}
      loading={loading}
      error={error}
    >
      {data && (
        <Box>
          {/* Tabs */}
          <Card sx={{ mb: 3, overflow: 'hidden' }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
              <Tabs
                value={tabValue}
                onChange={(_, newValue) => setTabValue(newValue)}
                variant={isMobile ? "fullWidth" : "scrollable"}
                scrollButtons={isMobile ? false : "auto"}
                sx={{
                  minHeight: { xs: 40, sm: 48 },
                  '& .MuiTab-root': {
                    minWidth: { xs: 0, sm: 90 },
                    minHeight: { xs: 40, sm: 48 },
                    px: { xs: 0.5, sm: 2 },
                    py: { xs: 0.75, sm: 1.5 },
                    fontSize: { xs: '0.7rem', sm: '0.875rem' },
                    textTransform: 'none',
                  },
                }}
              >
                <Tab icon={<TableChart />} iconPosition="start" label={translate('reports.tabs.dataTable', { fallback: 'Data Table' })} />
                <Tab icon={<ShowChart />} iconPosition="start" label={translate('reports.tabs.charts', { fallback: 'Charts' })} />
              </Tabs>
            </Box>

            {/* Data Table Tab */}
            <TabPanel value={tabValue} index={0}>
              <Box sx={{ px: { xs: 1.5, sm: 3 }, py: { xs: 2, sm: 3 } }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#1A1A1A', mb: 2 }}>
                  {translate('reports.charts.hourlyDistribution', { fallback: 'Hourly Distribution' })} ({sortedData.length} hours)
                </Typography>

                <Box sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                <TableContainer>
                  <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ width: 'auto' }}>
                          <TableSortLabel
                            active={orderBy === 'hour'}
                            direction={orderBy === 'hour' ? order : 'asc'}
                            onClick={() => handleSort('hour')}
                          >
                            {translate('reports.table.hour', { fallback: 'Hour' })}
                          </TableSortLabel>
                        </TableCell>
                        {canSeeTotalSessions && (
                          <TableCell align="right" sx={{ width: { xs: 120, sm: 160 } }}>
                            <TableSortLabel
                              active={orderBy === 'sessions'}
                              direction={orderBy === 'sessions' ? order : 'asc'}
                              onClick={() => handleSort('sessions')}
                            >
                              {translate('reports.kpi.totalSessions', { fallback: 'Total Sessions' })}
                            </TableSortLabel>
                          </TableCell>
                        )}
                        <TableCell align="right" sx={{ width: { xs: 120, sm: 160 } }}>
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                            <TableSortLabel
                              active={orderBy === 'charged_sessions'}
                              direction={orderBy === 'charged_sessions' ? order : 'asc'}
                              onClick={() => handleSort('charged_sessions')}
                            >
                              {translate('reports.kpi.chargedSessions', { fallback: 'Charged Sessions' })}
                            </TableSortLabel>
                            <MuiTooltip
                              title={translate('reports.kpi.chargedSessionsTooltip', {
                                fallback: 'Sessions that actually delivered energy (kWh > 0).',
                              })}
                              arrow
                              placement="top"
                            >
                              <Box component="span" sx={{ cursor: 'help', color: '#9E9E9E' }}>ⓘ</Box>
                            </MuiTooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedData.map((row) => (
                        <TableRow key={row.hour}>
                          <TableCell>{row.hour_label}</TableCell>
                          {canSeeTotalSessions && (
                            <TableCell align="right">{formatNumber(row.sessions, 0)}</TableCell>
                          )}
                          <TableCell align="right">{formatNumber(row.charged_sessions, 0)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                    <TableFooter>
                      <TableRow
                        sx={{
                          '& td': {
                            position: 'sticky',
                            bottom: 0,
                            backgroundColor: '#F5F5F5',
                            borderTop: '2px solid #E0E0E0',
                            fontWeight: 600,
                            zIndex: 2,
                          }
                        }}
                      >
                        <TableCell>{translate('reports.summary', { fallback: 'Summary' })}</TableCell>
                        {canSeeTotalSessions && (
                          <TableCell align="right">{formatNumber(totals.sessions, 0)}</TableCell>
                        )}
                        <TableCell align="right">{formatNumber(totals.charged_sessions, 0)}</TableCell>
                      </TableRow>
                    </TableFooter>
                  </Table>
                </TableContainer>
                </Box>
              </Box>
            </TabPanel>

            {/* Charts Tab */}
            <TabPanel value={tabValue} index={1}>
              <Box sx={{ px: { xs: 1.5, sm: 3 }, pb: { xs: 2, sm: 3 } }}>
                {/* Hourly Distribution Chart */}
                <Box sx={{ backgroundColor: '#FAFAFA', borderRadius: 2, p: { xs: 2, sm: 3 }, mb: 3, mt: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#1A1A1A', mb: 3 }}>
                    {translate('reports.charts.hourlyDistribution', { fallback: 'Hourly Distribution' })}
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={data.hourly_distribution}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                      <XAxis
                        dataKey="hour_label"
                        tick={{ fontSize: 10 }}
                        stroke="#606060"
                        angle={-45}
                        textAnchor="end"
                        height={80}
                      />
                      <YAxis
                        tick={{ fontSize: 12 }}
                        stroke="#606060"
                        tickFormatter={(value) => formatNumber(value, 0)}
                      />
                      <Tooltip
                        formatter={(value: number) => formatNumber(value, 0)}
                        contentStyle={{
                          backgroundColor: '#FFFFFF',
                          border: '1px solid #E0E0E0',
                          borderRadius: 8,
                        }}
                      />
                      <Legend />
                      {canSeeTotalSessions && (
                        <Bar dataKey="sessions" fill="#009A75" name={translate('reports.kpi.totalSessions', { fallback: 'Total Sessions' })} radius={[8, 8, 0, 0]} />
                      )}
                      <Bar dataKey="charged_sessions" fill="#7E57C2" name={translate('reports.kpi.chargedSessions', { fallback: 'Charged Sessions' })} radius={[8, 8, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>

                {/* Weekly Heatmap */}
                <Box sx={{ backgroundColor: '#FAFAFA', borderRadius: 2, p: { xs: 2, sm: 3 } }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#1A1A1A', mb: 3 }}>
                    {translate('reports.charts.hourlyUsagePattern', { fallback: 'Hourly Usage Pattern' })}
                  </Typography>
                  <UsageHeatmap data={transformHeatmapData(data.weekly_heatmap)} loading={loading} />
                </Box>
              </Box>
            </TabPanel>
          </Card>
        </Box>
      )}
    </ReportLayout>
  );
};
